/**
 * FAQ catalog (EN + per-locale overrides). Mirrors data/articles.ts pattern.
 * Locale fallback: target locale → English. Translation files are added one
 * at a time as `data/faq-{ru,es,pt,tr}.json`; until they ship, all locales
 * render the EN copy.
 *
 * Vite's import.meta.glob resolves matching files at build time. Missing
 * locales simply don't appear in the map → fall through to EN.
 */
import { buildFaqFigures, resolveFaqPair, type FaqCategory } from '@roamzy/shared';

import enRaw from '../../../data/faq.json';
import { allCountries } from './prices';
import { getLocale } from '../i18n';
import type { Locale } from '../i18n/types';

export interface FaqQa {
  id: string;
  cat: FaqCategory;
  q: string;
  a: string;
}

export interface FaqLabels {
  title: string;
  kicker: string;
  lede: string;
  volume: string;
  edition: string;
  filterAll: string;
  filterSetup: string;
  filterPricing: string;
  filterAccount: string;
  filterCrypto: string;
  filterSupport: string;
  chapter: string;
  chapterOne: string;
  chapterTwo: string;
  chapterThree: string;
  chapterFour: string;
  chapterFive: string;
  groupSetup: string;
  groupPricing: string;
  groupAccount: string;
  groupCrypto: string;
  groupSupport: string;
  stillCurious: string;
  askDirectly: string;
  supportLine: string;
  supportCta: string;
}

export interface FaqCatalog {
  labels: FaqLabels;
  qas: FaqQa[];
}

// Vite scans matching JSON files and inlines them — no runtime fetches.
// Pattern: `../../../data/faq-{locale}.json`. EN is loaded directly above
// (it's the source of truth and always present).
const localeModules = import.meta.glob<{ default: FaqCatalog }>(
  '../../../data/faq-*.json',
  { eager: true },
);

const byLocale: Partial<Record<Locale, FaqCatalog>> = {
  en: enRaw as unknown as FaqCatalog,
};
for (const [path, mod] of Object.entries(localeModules)) {
  const m = path.match(/faq-([a-z]{2})\.json$/);
  if (m) byLocale[m[1] as Locale] = mod.default;
}

/**
 * Cost answers in data/faq.json carry `{{tokens}}` instead of typed prices —
 * the JSON is static, the catalog is not. The figures are computed from
 * data/prices.json (the same file the rate table renders from) by the shared
 * builder the backend uses for the SSR body and the FAQPage JSON-LD, so the
 * pre-hydration paint and this SPA re-render show identical numbers.
 *
 * An entry whose tokens cannot all be resolved is dropped, never shown with a
 * raw token. Locale catalogs that carry no tokens pass through unchanged.
 */
const FIGURES = buildFaqFigures(allCountries);

function resolveCatalog(cat: FaqCatalog): FaqCatalog {
  const qas: FaqQa[] = [];
  for (const qa of cat.qas) {
    const resolved = resolveFaqPair(qa.q, qa.a, FIGURES);
    if (!resolved) continue;
    qas.push({ ...qa, q: resolved.q, a: resolved.a });
  }
  return { labels: cat.labels, qas };
}

const resolvedByLocale = new Map<Locale, FaqCatalog>();

export function getFaq(): FaqCatalog {
  const loc = getLocale();
  const cached = resolvedByLocale.get(loc);
  if (cached) return cached;
  const raw = byLocale[loc] ?? (byLocale.en as FaqCatalog);
  const out = resolveCatalog(raw);
  resolvedByLocale.set(loc, out);
  return out;
}
